import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { Spin } from 'antd';
import type { ReactElement } from 'react';

import type { ChatAgentCapabilityItem } from '../api/chat.client.js';
import { localizedCapabilityDescription, localizedToolName } from '../lib/aiUiLabels.js';

export interface AgentCapabilitiesCardProps {
  capabilities: ChatAgentCapabilityItem[];
  summary: {
    total: number;
    available: number;
    readOnly: number;
    approvalRequired: number;
  };
  isLoading?: boolean;
}

function getSafetyLabel(capability: ChatAgentCapabilityItem): string {
  if (!capability.available) {
    return i18n.t('crm.features.features.ai.chat.components.agentcapabilitiescard.nedostupno');
  }

  return capability.safety === 'approval_required'
    ? i18n.t('crm.features.features.ai.chat.components.agentcapabilitiescard.s_podtverzhdeniem')
    : 'Read-only';
}

function getSafetyClassName(capability: ChatAgentCapabilityItem): string {
  if (!capability.available) {
    return 'border-stone-200 bg-stone-100 text-stone-500';
  }

  return capability.safety === 'approval_required'
    ? 'border-amber-200 bg-amber-50 text-amber-800'
    : 'border-emerald-200 bg-emerald-50 text-emerald-700';
}

export function AgentCapabilitiesCard({
  capabilities,
  summary,
  isLoading = false,
}: AgentCapabilitiesCardProps): ReactElement {
  useI18nSubscription();
  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-4"
      data-testid="agent-capabilities-card"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {i18n.t(
              'crm.features.features.ai.chat.components.agentcapabilitiescard.agentnaya_logika',
            )}
          </p>
          <p className="mt-1 text-[12px] leading-5 text-stone-600">
            {i18n.t(
              'crm.features.features.ai.chat.components.agentcapabilitiescard.zhivoy_katalog_tools_iz_backend_chtenie_vypo',
            )}
          </p>
        </div>
        <span className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
          {summary.available}/{summary.total}
        </span>
      </div>

      {isLoading ? (
        <div className="flex min-h-16 items-center justify-center">
          <Spin size="small" />
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg bg-stone-50 px-2 py-1.5 text-stone-600">
              Read-only: <strong className="text-stone-900">{summary.readOnly}</strong>
            </div>
            <div className="rounded-lg bg-stone-50 px-2 py-1.5 text-stone-600">
              Approval: <strong className="text-stone-900">{summary.approvalRequired}</strong>
            </div>
          </div>
          {capabilities.length > 0 ? (
            <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {capabilities.map((capability) => (
                <li
                  key={capability.name}
                  className="rounded-lg border border-stone-100 bg-stone-50 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 break-words text-[12px] font-semibold text-stone-800">
                      {localizedToolName(capability.name, i18n.t)}
                    </span>
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getSafetyClassName(
                        capability,
                      )}`}
                    >
                      {getSafetyLabel(capability)}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-stone-500">
                    {localizedCapabilityDescription(
                      capability.name,
                      capability.description,
                      i18n.t,
                    )}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] leading-5 text-stone-500">
              {i18n.t(
                'crm.features.features.ai.chat.components.agentcapabilitiescard.dlya_tekushchikh_prav_net_dostupnykh_agent_tools',
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
